import React, { Dispatch, FunctionComponent, SetStateAction, useEffect } from 'react'
import { IPostRenewableEnergyCertificate } from '../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.model'
import { Box, InputAdornment, Stack } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { useNavigate } from 'react-router-dom';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormFileInputDisplay } from '../form-field-components/new-and-edit/form-file-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { FormDateInputDisplay } from '../form-field-components/new-and-edit/form-date-input';
import moment from 'moment';

export const RenewableEnergyCertificateForm: FunctionComponent<{
  newRenewableEnergyCertificate: IPostRenewableEnergyCertificate & { uploadedResources?: any[] },
  submitHandler: React.Dispatch<React.SetStateAction<any>>,
  isEditPage?: boolean,
  renewableEnergyCertificateActive?: boolean,
  isSubmitting?: boolean | undefined,
  editField?: string,
  setEditField?: React.Dispatch<React.SetStateAction<any>>,
  errorMessage?: string,
  errorCode?: number,
  setErrorMessage?: Dispatch<SetStateAction<string | undefined>>,
  setErrorCode?: Dispatch<SetStateAction<number | undefined>>,
  energyKwhUsed?: number,
}> = ({
  newRenewableEnergyCertificate,
  submitHandler,
  isEditPage = false,
  renewableEnergyCertificateActive,
  isSubmitting,
  editField,
  setEditField,
  errorMessage,
  errorCode,
  setErrorMessage,
  setErrorCode,
  energyKwhUsed,
}) => {
    const navigate = useNavigate()
    //State 
    const [recFileData, setRecFileData] = React.useState<any[]>([]);
    const [removedResourceIds, setRemovedResourceIds] = React.useState<string[]>([]);
    //Formik
    const renewableEnergyCertificateSchema = Yup.object().shape({
      certificateType: Yup.string().required(`You must provide a certificate type for the renewable energy certificate`),
      certificateNumber: Yup.string().required(`You must provide a certificate number for the renewable energy certificate`),
      name: Yup.string().nullable(),
      registry: Yup.string().required(`You must provide a registry for the renewable energy certificate`),
      generationPeriod: Yup.string().required(`You must provide a generation period for the renewable energy certificate`),
      source: Yup.string().nullable(),
      sourceOther: Yup.string().when('source', {
        is: 'Other',
        then: (schema) => schema.required('You must specify the other source'),
        otherwise: (schema) => schema.nullable()
      }),
      energyMwh: Yup.number().required(`You must provide an energy (MWh) for the renewable energy certificate`).min(0, `The energy (MWh) must be greater than 0`),
      retired: Yup.boolean().required(`You must provide a retired status for the renewable energy certificate`),
      retiredDate: Yup.string().nullable(),
      notes: Yup.string().nullable(),

    })

    const formik = useFormik<IPostRenewableEnergyCertificate>({
      initialValues: {
        name: newRenewableEnergyCertificate.name,
        notes: newRenewableEnergyCertificate.notes,
        certificateNumber: newRenewableEnergyCertificate.certificateNumber,
        registry: newRenewableEnergyCertificate.registry,
        energyMwh: newRenewableEnergyCertificate.energyMwh,
        generationPeriod: newRenewableEnergyCertificate.generationPeriod,
        certificateType: newRenewableEnergyCertificate.certificateType,
        source: newRenewableEnergyCertificate.source,
        sourceOther: newRenewableEnergyCertificate.sourceOther,
        retired: newRenewableEnergyCertificate.retired,
        retiredDate: newRenewableEnergyCertificate.retiredDate,
      },
      validationSchema: renewableEnergyCertificateSchema,
      onSubmit: (values) => {
        submitHandler({ values, files: recFileData, removedResourceIds })
      }
    });

    //Use Effects
    useEffect(() => {
      if (!isEditPage) {
        formik.setValues({
          name: newRenewableEnergyCertificate.name,
          notes: newRenewableEnergyCertificate.notes,
          certificateNumber: newRenewableEnergyCertificate.certificateNumber,
          registry: newRenewableEnergyCertificate.registry,
          generationPeriod: newRenewableEnergyCertificate.generationPeriod,
          energyMwh: newRenewableEnergyCertificate.energyMwh,
          certificateType: newRenewableEnergyCertificate.certificateType,
          retired: newRenewableEnergyCertificate.retired,
          retiredDate: newRenewableEnergyCertificate.retiredDate,
          source: newRenewableEnergyCertificate.source,
          sourceOther: newRenewableEnergyCertificate.sourceOther,
        })
      }
    }, [newRenewableEnergyCertificate, isEditPage])

    // Renewable source options depend on the certificate type: gas certificates (RGGO) use
    // gas-derived sources, all others use the electricity source list.
    const gasSources = ['Biomethane', 'Biogas', 'Other']
    const electricitySources = ['Solar', 'Wind', 'Hydro', 'Biomass', 'Geothermal', 'Ocean / Tidal', 'Other']
    const isRggo = formik.values.certificateType === 'RGGO'
    const sourceOptions = isRggo ? gasSources : electricitySources

    // Auto-fill the issuing registry when blank, per certificate type.
    const defaultRegistryByType: { [type: string]: string } = {
      REGO: 'Ofgem',
      RGGO: 'Green Gas Certification Scheme',
    }
    useEffect(() => {
      const defaultRegistry = defaultRegistryByType[formik.values.certificateType]
      const currentRegistry = formik.values.registry || ''
      if (defaultRegistry && currentRegistry.trim() === '') {
        formik.setFieldValue('registry', defaultRegistry)
      }
      // Clear a source that doesn't belong to the newly selected type's options.
      const currentSource = formik.values.source || ''
      if (currentSource && !sourceOptions.includes(currentSource)) {
        formik.setFieldValue('source', '')
        formik.setFieldValue('sourceOther', '')
      }
    }, [formik.values.certificateType, formik.values.registry])

    //Formik Event Handlers
    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (newRenewableEnergyCertificate) {
        formik.setValues({
          name: newRenewableEnergyCertificate.name,
          notes: newRenewableEnergyCertificate.notes,
          certificateNumber: newRenewableEnergyCertificate.certificateNumber,
          registry: newRenewableEnergyCertificate.registry,
          energyMwh: newRenewableEnergyCertificate.energyMwh,
          generationPeriod: newRenewableEnergyCertificate.generationPeriod,
          certificateType: newRenewableEnergyCertificate.certificateType,
          source: newRenewableEnergyCertificate.source,
          retired: newRenewableEnergyCertificate.retired,
          retiredDate: newRenewableEnergyCertificate.retiredDate,
          sourceOther: newRenewableEnergyCertificate.sourceOther,
        })
        setRecFileData([])
        setRemovedResourceIds([])
      }
    }

    const removeRecFileHandler = () => {
      setRecFileData([]);
    }

    const dateHandler = (date: any, fieldName: string) => {
      if (fieldName === 'generationPeriod') {
        formik.setFieldValue(fieldName, date ? date.format('YYYY-MM') : '');
      } else {
        formik.setFieldValue(fieldName, date ? date.format('YYYY-MM-DD') : '');
      }
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'certificateNumber'}
          title={`Certificate Number`}
          name='certificateNumber'
          isTopRow={true}
          value={formik.values.certificateNumber}
          touched={formik.touched.certificateNumber}
          errors={formik.errors.certificateNumber}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
        />
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'certificateType'}
          title='Certificate Type'
          name='certificateType'
          value={formik.values.certificateType}
          touched={formik.touched.certificateType}
          errors={formik.errors.certificateType}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
          array={['REC', 'REGO', 'GoO', 'RGGO']}
          valueKey={''}
          labelKey={(type) => type}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'name'}
          title='Certificate Name'
          name='name'
          value={formik.values.name}
          touched={formik.touched.name}
          errors={formik.errors.name}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'registry'}
          title='Issuing Registry'
          name='registry'
          value={formik.values.registry}
          touched={formik.touched.registry}
          errors={formik.errors.registry}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 4 ? errorMessage : undefined}
          hideEditIcon={!renewableEnergyCertificateActive}
        />
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'source'}
          title='Source'
          name='source'
          value={formik.values.source}
          touched={formik.touched.source}
          errors={formik.errors.source}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
          array={sourceOptions}
          valueKey={''}
          labelKey={(type) => type}
        />
        {formik.values.source === 'Other' && (
          <FormTextInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'sourceOther'}
            title='Source Other'
            name='sourceOther'
            topBorder={false}
            value={formik.values.sourceOther}
            touched={formik.touched.sourceOther}
            errors={formik.errors.sourceOther}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!renewableEnergyCertificateActive}
          />
        )}
        <FormDateInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'generationPeriod'}
          title='Generation Period'
          name='generationPeriod'
          value={formik.values.generationPeriod ? moment(formik.values.generationPeriod) : null}
          touched={formik.touched.generationPeriod}
          errors={formik.errors.generationPeriod}
          onChange={(date: any) => dateHandler(date, 'generationPeriod')}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
          monthYearOnly={true}
          customErrorMessage={errorCode === 5 ? errorMessage : undefined}
        />

        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'energyMwh'}
          title='Energy Amount'
          name='energyMwh'
          type='number'
          endAdornment={<InputAdornment position='end'>MWh</InputAdornment>}
          value={formik.values.energyMwh}
          touched={formik.touched.energyMwh}
          errors={formik.errors.energyMwh}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 3 ? errorMessage : undefined}
          hideEditIcon={!renewableEnergyCertificateActive}
          chip={isEditPage && energyKwhUsed && !newRenewableEnergyCertificate.retired ? `Used ${energyKwhUsed / 1000} MWh` : undefined}
        />
        {/* {isEditPage && !renewableEnergyCertificateActive &&
          <FormDateInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'retiredDate'}
            title='Retired Date'
            name='retiredDate'
            value={moment(formik.values.retiredDate)}
            touched={formik.touched.retiredDate}
            errors={formik.errors.retiredDate}
            onChange={(date: any) => dateHandler(date, 'retiredDate')}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!renewableEnergyCertificateActive}
            allowEdit={false}
          />} */}
        <FormFileInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'recFilePath'}
          title='Certificate File'
          name='recFilePath'
          value={undefined}
          onChange={() => { }}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
          fileData={recFileData}
          setFileData={setRecFileData}
          onFileUploadSubmit={() => { }}
          onImageRemove={removeRecFileHandler}
          allowedFileTypes={['PDF', 'XLS', 'XLSX', 'CSV', 'JPG', 'PNG', 'GIF', 'JPEG']}
          uploadButtonText='Upload File'
          uploadedResources={newRenewableEnergyCertificate.uploadedResources}
          removedResourceIds={removedResourceIds}
          onToggleResourceRemove={(id: string, removed: boolean) => {
            setRemovedResourceIds(prev => removed ? [...prev, id] : prev.filter(x => x !== id))
          }}
          customErrorMessage={errorCode === 5 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isBottomRow={true}
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'notes'}
          title='Notes'
          name='notes'
          value={formik.values.notes}
          touched={formik.touched.notes}
          errors={formik.errors.notes}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!renewableEnergyCertificateActive}
        />
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate('/organisation/renewable-energy-certificates')}
              submitHandler={formik.submitForm}
              disableSaveButton={recFileData.length === 0}
            />
          </Stack>
        }
      </Box >
    )
  }